import { useEffect, useState } from 'react'
import { UserAvatar } from '@/components/user-avatar'
import { Badge } from '@journ/ui/badge'
import { Button } from '@journ/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@journ/ui/dropdown-menu'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@journ/ui/select'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@journ/ui/sheet'
import { SubmitButton } from '@journ/ui/submit-button'
import { Table, TableBody, TableCell, TableRow } from '@journ/ui/table'
import { Icons } from '@journ/ui/icons'
import { useIntlayer } from 'react-intlayer'
import { Container } from '@/components/container'
import { Sheet as ModalSheet } from '@/components/sheet'
import { useMembers } from '@/features/hr/hooks/use-members'
import { useOrgTeams } from '@/features/hr/hooks/use-org-teams'
import { useOrgTeamActions } from '@/features/hr/hooks/use-org-team-actions'
import { refreshMembersList } from '@/features/hr/store'
import type { OrgTeam } from '@/features/hr/domain/member'
import type { TeamMember } from '@/features/hr/domain/member'

const NO_MEMBER = '__none__'

const TeamsPage = () => {
	const content = useIntlayer('teams-page')
	const { teams, reload: reloadTeams } = useOrgTeams()
	const { members } = useMembers(null, 'active')
	const { addTeam, editTeam, removeTeam, addTeamMember, removeTeamMember, addTeamLeader, removeTeamLeader } =
		useOrgTeamActions()

	const [search, setSearch] = useState('')
	const [showCreate, setShowCreate] = useState(false)
	const [editingTeam, setEditingTeam] = useState<OrgTeam | null>(null)
	const [deleteTarget, setDeleteTarget] = useState<OrgTeam | null>(null)
	const [managingTeam, setManagingTeam] = useState<OrgTeam | null>(null)

	const reload = async () => {
		await reloadTeams()
		refreshMembersList()
	}

	const filtered = teams.filter(t => t.name.toLowerCase().includes(search.toLowerCase()))

	const handleCreate = async (name: string) => {
		await addTeam({ name, departmentId: null, memberIds: [], leaderIds: [] })
		await reload()
		setShowCreate(false)
	}

	const handleRename = async (id: string, name: string) => {
		await editTeam(id, { name })
		await reload()
		setEditingTeam(null)
	}

	const handleDelete = async (id: string) => {
		await removeTeam(id)
		await reload()
		setDeleteTarget(null)
	}

	const handleAddMember = async (teamId: string, memberId: string) => {
		await addTeamMember(teamId, memberId)
		await reload()
		setManagingTeam(prev => (prev ? { ...prev, memberIds: [...prev.memberIds, memberId] } : prev))
	}

	const handleRemoveMember = async (teamId: string, memberId: string) => {
		await removeTeamMember(teamId, memberId)
		await reload()
		setManagingTeam(prev =>
			prev
				? {
						...prev,
						memberIds: prev.memberIds.filter(id => id !== memberId),
						leaderIds: prev.leaderIds.filter(id => id !== memberId),
					}
				: prev,
		)
	}

	const handleToggleLeader = async (teamId: string, memberId: string, isLeader: boolean) => {
		if (isLeader) {
			await removeTeamLeader(teamId, memberId)
			setManagingTeam(prev => (prev ? { ...prev, leaderIds: prev.leaderIds.filter(id => id !== memberId) } : prev))
		} else {
			await addTeamLeader(teamId, memberId)
			setManagingTeam(prev => (prev ? { ...prev, leaderIds: [...prev.leaderIds, memberId] } : prev))
		}
		await reload()
	}

	const currentManaging = managingTeam ? (teams.find(t => t.id === managingTeam.id) ?? managingTeam) : null

	return (
		<div className='space-y-6 mt-4'>
			<div className='flex items-center space-x-4'>
				<Input
					className='flex-1'
					placeholder='Search...'
					value={search}
					onChange={e => setSearch(e.target.value)}
					autoComplete='off'
					autoCapitalize='none'
					autoCorrect='off'
					spellCheck={false}
				/>
				<Button onClick={() => setShowCreate(true)}>{content.newTeam}</Button>
			</div>

			<Table>
				<TableBody>
					{filtered.length === 0 && (
						<TableRow className='hover:bg-transparent'>
							<TableCell className='h-24 text-center text-sm text-muted-foreground' colSpan={2}>
								{content.noTeams}
							</TableCell>
						</TableRow>
					)}
					{filtered.map(team => {
						const teamMembers = members.filter(m => team.memberIds.includes(m.id))
						return (
							<TableRow key={team.id} className='hover:bg-transparent'>
								<TableCell className='py-4'>
									<div className='flex flex-col'>
										<span className='font-medium text-sm'>{team.name}</span>
										<span className='text-sm text-606060'>
											{teamMembers.length} {teamMembers.length === 1 ? 'member' : 'members'}
											{team.leaderIds.length > 0 &&
												` · ${team.leaderIds.length} leader${team.leaderIds.length > 1 ? 's' : ''}`}
										</span>
									</div>
								</TableCell>
								<TableCell className='text-right py-4'>
									<div className='flex items-center justify-end space-x-2'>
										<Button variant='outline' size='sm' onClick={() => setManagingTeam(team)}>
											{content.manage}
										</Button>
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant='ghost' className='h-8 w-8 p-0'>
													<Icons.MoreHoriz size={16} />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align='end'>
												<DropdownMenuItem onClick={() => setEditingTeam(team)}>{content.rename}</DropdownMenuItem>
												<DropdownMenuItem className='text-destructive' onClick={() => setDeleteTarget(team)}>
													{content.delete}
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</div>
								</TableCell>
							</TableRow>
						)
					})}
				</TableBody>
			</Table>

			<TeamFormDialog
				isOpen={showCreate}
				title={content.newTeam.value}
				content={content}
				onConfirm={handleCreate}
				onCancel={() => setShowCreate(false)}
			/>

			<TeamFormDialog
				isOpen={!!editingTeam}
				title={content.rename.value}
				initialName={editingTeam?.name}
				content={content}
				onConfirm={name => editingTeam && handleRename(editingTeam.id, name)}
				onCancel={() => setEditingTeam(null)}
			/>

			<DeleteTeamDialog
				isOpen={!!deleteTarget}
				content={content}
				onConfirm={() => deleteTarget && handleDelete(deleteTarget.id)}
				onCancel={() => setDeleteTarget(null)}
			/>

			{currentManaging && (
				<ManageTeamSheet
					team={currentManaging}
					allMembers={members}
					content={content}
					onClose={() => setManagingTeam(null)}
					onAddMember={memberId => handleAddMember(currentManaging.id, memberId)}
					onRemoveMember={memberId => handleRemoveMember(currentManaging.id, memberId)}
					onToggleLeader={(memberId, isLeader) => handleToggleLeader(currentManaging.id, memberId, isLeader)}
				/>
			)}
		</div>
	)
}

type ManageTeamSheetProps = {
	team: OrgTeam
	allMembers: TeamMember[]
	content: ReturnType<typeof useIntlayer<'teams-page'>>
	onClose: () => void
	onAddMember: (memberId: string) => void
	onRemoveMember: (memberId: string) => void
	onToggleLeader: (memberId: string, isLeader: boolean) => void
}

const ManageTeamSheet = ({
	team,
	allMembers,
	content,
	onClose,
	onAddMember,
	onRemoveMember,
	onToggleLeader,
}: ManageTeamSheetProps) => {
	const [selectedMember, setSelectedMember] = useState(NO_MEMBER)
	const teamMembers = allMembers.filter(m => team.memberIds.includes(m.id))
	const available = allMembers.filter(m => !team.memberIds.includes(m.id))

	const handleAdd = () => {
		if (selectedMember === NO_MEMBER) return
		onAddMember(selectedMember)
		setSelectedMember(NO_MEMBER)
	}

	return (
		<Sheet open onOpenChange={open => !open && onClose()}>
			<SheetContent>
				<SheetHeader>
					<SheetTitle>{team.name}</SheetTitle>
				</SheetHeader>

				<div className='mt-6 space-y-6'>
					<Table>
						<TableBody>
							{teamMembers.length === 0 && (
								<TableRow className='hover:bg-transparent'>
									<TableCell className='text-sm text-muted-foreground text-center h-16' colSpan={2}>
										{content.noMembers}
									</TableCell>
								</TableRow>
							)}
							{teamMembers.map(member => {
								const isLeader = team.leaderIds.includes(member.id)
								return (
									<TableRow key={member.id} className='hover:bg-transparent'>
										<TableCell className='py-3'>
											<div className='flex items-center space-x-3'>
												<UserAvatar name={member.fullName} avatarUrl={member.avatarUrl} size='md' />
												<div className='flex flex-col'>
													<div className='flex items-center gap-2'>
														<span className='font-medium text-sm'>{member.fullName}</span>
														{isLeader && (
															<Badge variant='secondary' className='text-xs h-5'>
																{content.leaders}
															</Badge>
														)}
													</div>
													<span className='text-xs text-606060'>{member.email}</span>
												</div>
											</div>
										</TableCell>
										<TableCell className='text-right py-3 pr-0'>
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button variant='ghost' className='h-8 w-8 p-0'>
														<Icons.MoreHoriz size={16} />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align='end'>
													<DropdownMenuItem onClick={() => onToggleLeader(member.id, isLeader)}>
														{isLeader ? content.demoteLeader : content.promoteLeader}
													</DropdownMenuItem>
													<DropdownMenuItem className='text-destructive' onClick={() => onRemoveMember(member.id)}>
														{content.removeMember}
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										</TableCell>
									</TableRow>
								)
							})}
						</TableBody>
					</Table>

					{available.length > 0 && (
						<div className='flex gap-2'>
							<Select value={selectedMember} onValueChange={setSelectedMember}>
								<SelectTrigger className='flex-1'>
									<SelectValue placeholder={content.selectMember.value} />
								</SelectTrigger>
								<SelectContent>
									{available.map(m => (
										<SelectItem key={m.id} value={m.id}>
											{m.fullName}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<Button variant='outline' disabled={selectedMember === NO_MEMBER} onClick={handleAdd}>
								{content.addMember}
							</Button>
						</div>
					)}
				</div>
			</SheetContent>
		</Sheet>
	)
}

type TeamFormDialogProps = {
	isOpen: boolean
	title: string
	initialName?: string
	content: ReturnType<typeof useIntlayer<'teams-page'>>
	onConfirm: (name: string) => void
	onCancel: () => void
}

const TeamFormDialog = ({ isOpen, title, initialName, content, onConfirm, onCancel }: TeamFormDialogProps) => {
	const [name, setName] = useState(initialName ?? '')
	const [submitting, setSubmitting] = useState(false)

	useEffect(() => {
		setName(initialName ?? '')
	}, [initialName, isOpen])

	const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
		e.preventDefault()
		if (!name.trim()) return
		setSubmitting(true)
		try {
			onConfirm(name.trim())
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<ModalSheet isOpen={isOpen} toggle={onCancel}>
			<Container className='h-full flex items-center justify-center'>
				<div className='w-full max-w-sm bg-background border border-border p-6 space-y-4'>
					<h2 className='text-sm font-medium'>{title}</h2>
					<form onSubmit={handleSubmit} className='space-y-4'>
						<div className='space-y-1.5'>
							<Label>{content.teamName}</Label>
							<Input value={name} onChange={e => setName(e.target.value)} autoFocus autoComplete='off' />
						</div>
						<div className='flex justify-end gap-2'>
							<Button variant='outline' type='button' onClick={onCancel}>
								{content.cancel}
							</Button>
							<SubmitButton isSubmitting={submitting}>{content.save}</SubmitButton>
						</div>
					</form>
				</div>
			</Container>
		</ModalSheet>
	)
}

type DeleteTeamDialogProps = {
	isOpen: boolean
	content: ReturnType<typeof useIntlayer<'teams-page'>>
	onConfirm: () => void
	onCancel: () => void
}

const DeleteTeamDialog = ({ isOpen, content, onConfirm, onCancel }: DeleteTeamDialogProps) => {
	return (
		<ModalSheet isOpen={isOpen} toggle={onCancel}>
			<Container className='h-full flex items-center justify-center'>
				<div className='w-full max-w-sm bg-background border border-border p-6 space-y-4'>
					<div className='flex items-start justify-between'>
						<div className='space-y-1'>
							<h2 className='text-sm font-medium'>{content.deleteConfirm}</h2>
							<p className='text-sm text-muted-foreground'>{content.deleteDescription}</p>
						</div>
						<Button variant='ghost' size='icon' className='h-6 w-6 -mt-1 -mr-2' onClick={onCancel}>
							<Icons.Close size={16} />
						</Button>
					</div>
					<div className='flex justify-end gap-2'>
						<Button variant='outline' onClick={onCancel}>
							{content.cancel}
						</Button>
						<Button variant='destructive' onClick={onConfirm}>
							{content.delete}
						</Button>
					</div>
				</div>
			</Container>
		</ModalSheet>
	)
}

export { TeamsPage }
